import { LowerCasePipe } from '@angular/common';
import type { OnInit } from '@angular/core';
import { Component, DestroyRef, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslatePipe } from '@ngx-translate/core';
import {
  OnyxIconButtonComponent,
  OnyxIconComponent,
  OnyxModalService,
  OnyxSpinnerComponent,
} from '@onyx/angular';
import { concatMap, from, of, switchMap } from 'rxjs';
import { StorageService } from '../../../../../../common/services/storage.service';
import type { DriverReportModalCarouselData } from '../driver-report-modal-carousel/driver-report-modal-carousel.component';
import { DriverReportModalCarouselComponent } from '../driver-report-modal-carousel/driver-report-modal-carousel.component';
import type { DriverReportModalFileData } from '../driver-report-modal.component';

@Component({
  selector: 'app-driver-report-modal-image',
  imports: [
    TranslatePipe,
    LowerCasePipe,
    OnyxSpinnerComponent,
    OnyxIconButtonComponent,
    OnyxIconComponent,
  ],
  templateUrl: './driver-report-modal-image.component.html',
  styleUrl: './driver-report-modal-image.component.scss',
  host: { '(click)': `openCarouselModal()` },
})
export class DriverReportModalImageComponent implements OnInit {
  public readonly filesData = input.required<DriverReportModalFileData[]>();
  public readonly files = input.required<string[]>();
  public readonly title = input.required<string>();
  public readonly note = input<string | undefined>(undefined);

  private readonly storageService = inject(StorageService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly modalService = inject(OnyxModalService);

  protected loading = signal(false);
  protected previewImageData = signal<DriverReportModalFileData | null>(null);
  protected image = signal<string | null>(null);

  public ngOnInit(): void {
    of(this.files()[0])
      .pipe(
        switchMap((file) => this.storageService.getFile(file)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((image) => {
        this.image.set(URL.createObjectURL(image));
        this.previewImageData.set({
          files: this.files(),
          images: [image],
          title: this.title(),
          note: this.note(),
        });
      });
  }

  protected downloadFiles(): void {
    if (!this.files().length) return;

    this.loading.set(true);
    from(this.files())
      .pipe(
        concatMap((attachment) => this.storageService.downloadFile(attachment)),
      )
      .subscribe()
      .add(() => this.loading.set(false));
  }

  protected openCarouselModal(): void {
    const previewImage = this.previewImageData();
    if (!previewImage) return;

    this.modalService.open<DriverReportModalCarouselData>(
      DriverReportModalCarouselComponent,
      { data: this.filesData(), selected: previewImage },
    );
  }
}
